import Image from 'next/image';
import Link from 'next/link';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CurrencyInput, { CurrencyInputProps } from 'react-currency-input-field';
import { FaInstagram, FaRegEdit, FaTrash, FaUsers } from 'react-icons/fa';
import { BiPlayCircle, BiVideo } from 'react-icons/bi';
import { FiShare2, FiUsers } from 'react-icons/fi';
import { MdHomeFilled, MdOutlineMonetizationOn, MdOutlineVideoLibrary } from 'react-icons/md';
import { toast, ToastContainer } from 'react-toastify';
import { FreeMode, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { InsertImageButton } from 'components/InsertImageButton';
import { states, usePoolState } from './state';
import { isPoolVerified } from 'utils/verification';
import {
  AsideNavigation,
  ButtonDonnationValue,
  CenterLinks,
  CentredSpan,
  Container,
  ContainerDonationSuggestions,
  ContainerShare,
  Content,
  DonateInputContainer,
  EditableContent,
  FixedDonateResumeContainer,
  Footer,
  FooterSection,
  Header,
  HeaderSection,
  InstaLogo,
  LogoItems,
  MessageSection,
  MobileInstaLogo,
  PaginateContainer,
  PrivateAlbumSection,
  ProgressiveSection,
  PublicAlbumSection,
  QuotasSection,
  SecondNavBar,
  SendMessageSection,
  ShareSection,
  SubmitContainer,
  SupporterItem,
  SupporterList,
  Supporters,
  Switch,
  TaxValuesTextNote,
  TitleSection,
  VerificationContainer,
} from './styles';
import type { NavItensType, Pool, SocialNetworkType, Supportes } from './types';
import 'react-toastify/dist/ReactToastify.css';
import { ModalImageCropper } from 'components/ModalImageCropper';
import PaymentSecurity from 'components/PaymentSecurity';
import PoolProgressiveCard, { MountedData } from 'components/PoolProgressiveCard';
import ColorPickerIcon from '../../public/color-picker.png';
import { colors, getColorDistance, hexToRgb, similarityThreshold } from 'utils/colors';

import PreviewPhoto from '../../public/preview-photo.svg';
import { ReCAPTCHAProvider } from 'contexts/ReCAPTCHAContext';
import Button from 'components/Button';
import { SEO } from 'components/Seo';
import { EnvVars } from 'env';
import { ModalPoolPayment } from 'components/ModalPoolPayment';
import { usePool } from 'contexts/PoolContext';

import BgDefaultImage from 'public/pool/default-header-bg-support.png';
import BgFooterDefaultImage from 'public/pool/default-footer-bg.png';
import BgDefaultMobileImage from 'public/pool/default-header-mobile-bg-support.png';
import BgFooterDefaultMobileImage from 'public/pool/default-footer-mobile-bg.png';

import SocialShareModal from 'components/SocialShareModal';
import { useLetterCounter } from 'hooks/useLetterCounter';
import { useLoader } from 'contexts/Loader';
import FontEditor from 'components/FontEditor';
import { formatarValorParaReais } from 'utils/CurrencyFormat';
import { useRouter } from 'next/router';
import { ModalPoolVideo } from 'components/ModalPoolVideo';
import PaginationButtons from 'components/Pagination';
import { useAuth } from 'contexts/AuthContext';
import { api } from 'services/apiClient';
import { isPoolManagerUser } from 'utils/isPoolManagerUser';
import { readSupporterPaymentRaw, supporterPaymentViaLine } from 'utils/supporterPaymentVia';
import { ModalEndCampaign } from 'components/ModalEndCampaign';
import { ModalEndCampaignSuccess } from 'components/ModalEndCampaignSuccess';

function sanitizeDisplayName(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  return trimmed
    .replace(/\bundefined\b\s*[.,;:!?]*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const PoolTemplate: FC<{ isGuest?: boolean; data?: Pool; mode?: 'normal' | 'edit' }> = ({ mode = 'normal', isGuest, data }) => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [lastValue, setLastValue] = useState(0);
  const [secondNav, setSecondNav] = useState<NavItensType[]>([] as NavItensType[]);
  const [footerBgImage, setFooterBgImage] = useState('');
  const [modalPoolViedo, setModalPoolViedo] = useState(false);
  const pickColor = useRef(null);
  const currentURL = typeof window !== 'undefined' ? window.location.href.split('/pool/').join('/') : '';
  const {
    amount,
    setAmount,
    fetchAddedSupporters,
    supportes,
    currentPage,
    totalSupportesPages,
    handleNextSupportesPage,
    handlePreviewSupportesPage,
  } = usePool();
  const [publicAlbumPages, setPublicAlbumPages] = useState(1);
  const route = useRouter();
  const {
    categories,
    setSnapData,
    modalSocialShare,
    setModalSocialShare,
    pool,
    setPool,
    imgsPool,
    cropperSize,
    fontColor,
    newBackGroundColor,
    setNewBackGroundColor,
    messageForm,
    handlerPool,
    formData,
    setFormData,
    messages,
    modalImageCropper,
    setModalImageCropper,
    submit,
    handlerCropImage,
    getCropperSize,
    toggleModalImageCropper,
    setMessageForm,
    handlerSendMessage,
    handleModerateMessage,
    handleRemoveImg,
    handlerShareData,
    handlerTitleStyles,
    titleStyles,
    onCategoryChanged,
    handleVerifySlug,
    isValidSlug,
    setSelectedCategoryId,
    selectedCategoryId,
    handlerEditPool,
    handleFinishCampaign,
  } = usePoolState(isGuest, data);

  const { remaining, maxLenght, setWord: setTitleCounter } = useLetterCounter(70);
  const { setLoader } = useLoader();
  const { user } = useAuth();
  const isPoolRoute = route.pathname.startsWith('/pool');
  const showOwnerSupporterExtras = isPoolRoute && isPoolManagerUser(pool, user?.id);

  const [supporterPaymentHints, setSupporterPaymentHints] = useState<Record<string, string>>({});
  const supporterDetailFetchAttemptedRef = useRef(new Set<string>());

  const [showEndCampaignModal, setShowEndCampaignModal] = useState(false);
  const [showEndCampaignSuccessModal, setShowEndCampaignSuccessModal] = useState(false);

  const handleEndCampaignConfirm = async () => {
    const success = await handleFinishCampaign();
    if (success) {
      setShowEndCampaignModal(false);
      setShowEndCampaignSuccessModal(true);
    }
  };

  const descriptionContainerRef = useRef<HTMLDivElement>(null);
  const [isOnDescription, setIsOnDescription] = useState(false);
  const [isOnBottom, setIsOnBottom] = useState(true);
  const [progressiveData, setProgressiveData] = useState<MountedData>({
    raised: '0',
    supporters: 0,
    target: '0',
  });

  useEffect(() => {
    const handleScroll = () => {
      const descriptionContainer = descriptionContainerRef.current;

      if (!descriptionContainer) return;

      const rect = descriptionContainer.getBoundingClientRect();

      const containerEnded = rect.top * -1 <= rect.height - 438;

      setIsOnDescription(rect.top <= 0 && containerEnded);
      setIsOnBottom(rect.bottom <= 438);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleOnValueChange: CurrencyInputProps['onValueChange'] = (value): void => {
    if (!value) {
      setFormData({
        ...formData,
        target: 0,
      });
      return;
    }
    if (Number.isNaN(Number(value.replace(',', '.')))) {
      return;
    }
    setFormData({
      ...formData,
      target: value,
    });
    setLastValue(parseFloat(value));
  };

  useEffect(() => {
    handlerResetPool();
  }, []);

  const handlerResetPool = useCallback(() => {
    if (data) {
      data?.target &&
        setPool({
          ...data,
          target: parseFloat(data?.target.toString()) / 100,
        });

      data?.backgroundColor && setNewBackGroundColor(data?.backgroundColor);
      data?.footer && setFooterBgImage(data.footer);
      data.target && setLastValue(parseFloat(data.target.toString()) / 100);
      data.title && setTitleCounter(data.title);
      data.category?.id && setSelectedCategoryId(data.category?.id);

      data.id && fetchAddedSupporters(data.id);

      if (mode === 'edit') {
        setFormData({
          title: data.title,
          slug: data.slug,
          target: (parseFloat(data?.target as string) / 100).toString(),
          city: data.city,
          description: data.description,
          fontColor: data.fontColor,
          fontFamily: data.fontFamily,
          fixedFee: data.fixedFee,
          videoUrl: data.videoUrl,
          state: data.state,
        });
        setSnapData(
          JSON.stringify({
            title: data.title,
            slug: data.slug,
            target: (parseFloat(data?.target as string) / 100).toString(),
            city: data.city,
            description: data.description,
            fontColor: data.fontColor,
            fontFamily: data.fontFamily,
            fixedFee: data.fixedFee,
            videoUrl: data.videoUrl,
            state: data.state,
          }),
        );
      }
    }
    setLoader?.(false);
  }, [data, fetchAddedSupporters, mode, setFormData, setLoader, setNewBackGroundColor, setPool, setSnapData, setTitleCounter]);

  const nav: NavItensType[] = [
    {
      name: 'Como funciona',
      anchor: '/faq',
    },
    {
      name: 'Contato',
      anchor: '/contact',
    },
  ];

  const getSvgColor = useMemo(() => (newBackGroundColor === '#fff6e8' ? undefined : newBackGroundColor), [newBackGroundColor]);

  useEffect(() => {
    setSecondNav([
      {
        name: 'Home',
        anchor: '/profile/user',
        newPage: false,
        icon: <MdHomeFilled size={22} color={getSvgColor} />,
        iconMobile: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M23.3162 10.771V22.004C23.3154 22.3547 23.2202 22.6987 23.0407 22.9999C22.8613 23.3012 22.6041 23.5486 22.2961 23.7163C22.0137 23.872 21.6957 23.9515 21.3732 23.9471H15.5442C15.2865 23.9471 15.0394 23.8447 14.8572 23.6625C14.675 23.4803 14.5726 23.2332 14.5726 22.9755V17.1465C14.5726 16.8888 14.4703 16.6417 14.2881 16.4595C14.1059 16.2773 13.8588 16.175 13.6011 16.175H9.7151C9.45744 16.175 9.21034 16.2773 9.02814 16.4595C8.84595 16.6417 8.7436 16.8888 8.7436 17.1465V22.9755C8.7436 23.2332 8.64124 23.4803 8.45905 23.6625C8.27685 23.8447 8.02975 23.9471 7.77209 23.9471H1.94304C1.66741 23.9481 1.39474 23.8901 1.14339 23.777C0.892028 23.6639 0.6678 23.4983 0.485778 23.2913C0.169911 22.9181 -0.00234502 22.4444 2.41161e-05 21.9555V10.771C0.000109786 10.5014 0.0562971 10.2348 0.165013 9.98806C0.273729 9.74136 0.432594 9.51998 0.631504 9.338L10.3466 0.509424C10.7048 0.181734 11.1727 0 11.6581 0C12.1436 0 12.6115 0.181734 12.9697 0.509424L22.6847 9.338C22.8836 9.51998 23.0425 9.74136 23.1512 9.98806C23.2599 10.2348 23.3161 10.5014 23.3162 10.771Z"
              fill={getSvgColor}
            />
          </svg>
        ),
      },
      {
        name: 'Compartilhar',
        anchor: ``,
        newPage: true,
        icon: <FiShare2 color={getSvgColor} size={25} />,
        iconMobile: <FiShare2 color={getSvgColor} size={22} />,
      },
      {
        name: 'Financeiro',
        anchor: `/profile/gifts-received?pooId=${pool.id}`,
        icon: <MdOutlineMonetizationOn size={25} color={getSvgColor} />,
        iconMobile: <MdOutlineMonetizationOn size={25} color={getSvgColor} />,
        newPage: false,
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSvgColor]);

  const socialNetwork: SocialNetworkType[] = [
    {
      icon: '/instagram.png',
      url: 'https://www.instagram.com/nossavaquinha/',
    },
  ];

  let date = new Date();
  let year = date.getFullYear();

  const handleOnValueAmountChange: CurrencyInputProps['onValueChange'] = (value): void => {
    if (!value) {
      setAmount('');
      return;
    }
    if (Number.isNaN(Number(value.replace(',', '.')))) {
      return;
    }
    setAmount(value);
    setLastValue(parseFloat(value));
  };

  const { headerFontColor, headerBgColor } = useMemo(() => {
    var headerFontColor = '#174F68';
    var headerBgColor = getSvgColor || '#174F68';

    const targetColor = { r: 255, g: 255, b: 255 };
    const distance = getColorDistance(targetColor, hexToRgb(headerBgColor));

    if (distance < similarityThreshold) {
      return {
        headerBgColor,
        headerFontColor,
        distance,
      };
    } else {
      return {
        headerBgColor: !isGuest ? headerBgColor : '#ffffff',
        headerFontColor: !isGuest ? '#ffffff' : headerBgColor,
        distance,
      };
    }
  }, [getSvgColor, isGuest]);

  const { SecondHeaderbgColor, distance } = useMemo(() => {
    const targetColor = hexToRgb('#fff6e8');
    const distance = getColorDistance(targetColor, hexToRgb(getSvgColor || ''));

    if (distance < similarityThreshold) {
      return {
        SecondHeaderbgColor: '#174F68',
        distance,
      };
    } else {
      return {
        SecondHeaderbgColor: '#fff6e8',
        distance,
      };
    }
  }, [getSvgColor]);

  const auxColor = useMemo(() => {
    const targetColor = hexToRgb(newBackGroundColor);
    const distance = getColorDistance(targetColor, hexToRgb('#5cc9aa'));

    if (distance < 190 && newBackGroundColor !== '#fff6e8') {
      return '#000000';
    } else {
      return '#5cc9aa';
    }
  }, [newBackGroundColor]);

  function sortObjectKeys(obj: Record<string, any>): Record<string, any> {
    const sortedKeys = Object.keys(obj).sort();
    const sortedObject: Record<string, any> = {};

    for (const key of sortedKeys) {
      sortedObject[key] = obj[key];
    }

    return sortedObject;
  }

  const forceNavigation = (url: string) => {
    window.location.href = url;
  };

  const handleDonate = useCallback(() => {
    if (pool.active === false) {
      toast('Esta campanha foi finalizada e não aceita mais doações', {
        type: 'info',
      });
      return;
    }

    const value = parseFloat(amount);
    if (value < 20 || !value)
      toast('Valor inválido! Doação mínima R$ 20,00', {
        type: 'error',
      });
    else forceNavigation(`/payment/${pool.slug}?amount=${value}`);
  }, [amount, pool.slug, pool.active]);

  const handleDonateWithValue = useCallback((value: number) => {
    if (pool.active === false) {
      toast('Esta campanha foi finalizada e não aceita mais doações', {
        type: 'info',
      });
      return;
    }

    if (value < 20) {
      toast('Valor inválido! Doação mínima R$ 20,00', {
        type: 'error',
      });
      return;
    }
    forceNavigation(`/payment/${pool.slug}?amount=${value}`);
  }, [pool.slug, pool.active]);

  const isValidVideo = (url: string) =>
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|e\/|user\/.*#p\/.*\/|.*[?&]v=)|youtu\.be\/|vimeo\.com\/)([a-zA-Z0-9_-]+)/.test(
      url,
    );

  useEffect(() => {
    pool.id && fetchAddedSupporters(pool.id, currentPage);
  }, [currentPage, fetchAddedSupporters, pool.id]);

  useEffect(() => {
    supporterDetailFetchAttemptedRef.current.clear();
    setSupporterPaymentHints({});
  }, [pool.id, showOwnerSupporterExtras]);

  useEffect(() => {
    if (!showOwnerSupporterExtras || !pool.id) return;

    supportes.forEach((s) => {
      const id = s.id;
      if (!id || readSupporterPaymentRaw(s)) return;
      if (supporterDetailFetchAttemptedRef.current.has(id)) return;
      supporterDetailFetchAttemptedRef.current.add(id);
      api
        .get(`supporters/${id}`)
        .then(({ data }) => {
          const raw = readSupporterPaymentRaw(data);
          if (raw) setSupporterPaymentHints((prev) => ({ ...prev, [id]: raw }));
        })
        .catch(() => {
          supporterDetailFetchAttemptedRef.current.delete(id);
        });
    });
  }, [supportes, showOwnerSupporterExtras, pool.id]);

  return (
    <ReCAPTCHAProvider>
      {pool && (
        <>
          <SEO
            title={`Vaquinha ${EnvVars.SITE_NAME} - ${pool.slug}`}
            description={pool.description}
            excludeTitleSuffix
            image={`${EnvVars.URL}/Logo.svg`}
          />
        </>
      )}

      <ModalPoolVideo pool={pool} open={modalPoolViedo} setOpen={setModalPoolViedo} />

      <SocialShareModal url={currentURL} title={pool.slug} open={modalSocialShare} setOpen={setModalSocialShare} />

      <ModalEndCampaign
        modalIsOpen={showEndCampaignModal}
        closeModal={() => setShowEndCampaignModal(false)}
        onConfirm={handleEndCampaignConfirm}
      />

      <ModalEndCampaignSuccess
        modalIsOpen={showEndCampaignSuccessModal}
        closeModal={() => setShowEndCampaignSuccessModal(false)}
      />

      <Container template={undefined} background={getSvgColor} theme={undefined} auxColor={auxColor}>
        <div
          style={{
            boxShadow: isGuest ? '0 0.1rem 0.4rem rgba(0,0,0,0.5)' : undefined,
            background: !isGuest ? headerBgColor : headerFontColor,
          }}
        >
          <Header fontColor={pool.id && !isGuest ? headerFontColor : isGuest ? headerBgColor : '#ffffff'}>
            <div>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  forceNavigation('/');
                }}
              >
                <img src={distance < similarityThreshold && !isGuest ? '/Logo.svg' : '/Logo-white.svg'} height={60} alt="logo-white" />
              </a>
            </div>
            <div>
              <nav>
                <ul>
                  {nav.map(({ name, anchor }, index) => (
                    <li key={index}>
                      {name === 'Compartilhar' ? (
                        <button
                          onClick={() => {
                            handlerShareData();
                          }}
                          style={{
                            color: headerFontColor,
                          }}
                        >
                          Compartilhar
                        </button>
                      ) : (
                        <a
                          href={anchor}
                          onClick={(e) => {
                            e.preventDefault();
                            forceNavigation(anchor);
                          }}
                          style={{
                            color: headerFontColor,
                          }}
                          rel="noreferrer"
                        >
                          {name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>

              <button onClick={() => setToggleMenu((prev) => !prev)}>
                <Image height={16} width={30} src={'/menu.png'} alt="" />
              </button>
            </div>
          </Header>
          {!isGuest && (
            <SecondNavBar
              style={{
                backgroundColor: SecondHeaderbgColor,
              }}
              theme={undefined}
              fontColor={getSvgColor}
            >
              <div>
                <nav>
                  <ul>
                    {secondNav.map(({ name, anchor, icon, iconMobile, newPage }, index) => (
                      <li key={index}>
                        {name === 'Compartilhar' ? (
                          <ContainerShare
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              handlerShareData();
                            }}
                          >
                            {icon && <span>{icon}</span>}
                            <span>Compartilhar</span>
                            {iconMobile && <span>{iconMobile}</span>}
                          </ContainerShare>
                        ) : (
                          <a
                            href={anchor}
                            onClick={(e) => {
                              e.preventDefault();
                              newPage ? window.open(anchor, '_blank') : forceNavigation(anchor);
                            }}
                            rel="noreferrer"
                          >
                            {icon && <span>{icon}</span>}
                            <span>{name}</span>
                            {iconMobile && <span>{iconMobile}</span>}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </SecondNavBar>
          )}
          <AsideNavigation theme={undefined} toggle={toggleMenu} onClick={() => setToggleMenu((prev) => !prev)}>
            <nav onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#174f68', color: '#FFF6E8' }}>
              <h2>Menu</h2>
              <ul>
                {nav.map(({ name, anchor }, index) => (
                  <li key={index}>
                    <a
                      href={anchor}
                      onClick={(e) => {
                        e.preventDefault();
                        forceNavigation(anchor);
                      }}
                      rel="noreferrer"
                      style={{ color: '#FFF6E8' }}
                    >
                      {name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </AsideNavigation>
        </div>
        <Content auxColor={auxColor} color={fontColor} bgColor={newBackGroundColor} hasImage={!!pool.cover}>
          <HeaderSection
            cover={pool.cover || BgDefaultImage.src}
            bgColor={newBackGroundColor}
            mobileCover={pool.cover || BgDefaultMobileImage.src}
            hasCover={!!pool.cover}
          >
            {!isGuest && (
              <InsertImageButton onClick={() => toggleModalImageCropper('cover')} label="Alterar imagem de capa" arrowPosition="up" />
            )}
            {!isGuest && (
              <>
                <ModalImageCropper
                  cropperSize={getCropperSize()}
                  onCropImage={(file, url) => {
                    if (cropperSize === 'footer' && url) {
                      setFooterBgImage(url);
                    } else if (cropperSize === 'cover') {
                      setPool({
                        ...pool,
                        cover: url,
                      });
                    }
                    handlerCropImage(file);
                  }}
                  open={modalImageCropper}
                  setOpen={setModalImageCropper}
                  resizable={false}
                />
              </>
            )}
          </HeaderSection>
          <TitleSection
            style={{
              fontFamily: titleStyles?.fontFamily,
              color: titleStyles?.color,
              fontWeight: titleStyles?.fontWeight,
              fontStyle: titleStyles?.fontStyle,
              textDecoration: titleStyles?.textDecoration,
            }}
          >
            {(pool.title || formData.title) && (
              <FontEditor
                fireEvent={isGuest ? 'none' : 'click'}
                defaultValue={{
                  fontSize: '62.6726px',
                  fontFamily: 'inherit',
                  color: 'inherit',
                  fontWeight: 'inherit',
                  fontStyle: 'inherit',
                  textDecoration: 'inherit',
                }}
                withSize={false}
                label={
                  <h1>
                    {!isGuest ? (
                      <EditableContent
                        defaultValue={pool.title}
                        value={formData.title}
                        onChange={(e) => {
                          setFormData({ ...formData, title: e.target.value });
                          setTitleCounter(e.target.value);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        maxLength={70}
                      />
                    ) : (
                      formData.title ?? pool.title
                    )}
                    {!isGuest && (
                      <p
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          marginLeft: '2rem',
                          cursor: 'pointer',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: 'inherit',
                            border: 'solid 4px ' + fontColor + '11',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            padding: '1rem',
                            borderRadius: '50%',
                            height: 60,
                            width: 60,
                          }}
                        >
                          <FaRegEdit
                            color={fontColor}
                            height={25}
                            width={25}
                            style={{
                              marginLeft: 0,
                            }}
                          />
                          <label
                            style={{
                              fontFamily: 'Nunito',
                              fontSize: 12,
                              color: fontColor,
                            }}
                          >
                            Editar
                          </label>
                        </span>
                      </p>
                    )}
                  </h1>
                }
                onStyleChanged={(styles) => {
                  if (
                    JSON.stringify(sortObjectKeys(styles)) !==
                    JSON.stringify(
                      sortObjectKeys({
                        fontSize: '62.6726px',
                        fontFamily: 'inherit',
                        color: 'inherit',
                        fontWeight: 'inherit',
                        fontStyle: 'inherit',
                        textDecoration: 'inherit',
                      }),
                    )
                  )
                    handlerTitleStyles(styles);
                }}
              />
            )}
          </TitleSection>
          <ProgressiveSection fontColor={fontColor}>
            <div>
              {!isGuest ? (
                <div>
                  <div>
                    <strong>Título da vaquinha</strong>
                    <input
                      type="text"
                      defaultValue={pool ? pool.title : undefined}
                      value={formData.title}
                      onChange={(e) => {
                        setFormData({ ...formData, title: e.target.value });
                        setTitleCounter(e.target.value);
                      }}
                      maxLength={maxLenght}
                      placeholder="Ex. Ajuda para comprar veículo de trabalho"
                    />
                    <div
                      style={{
                        marginTop: '.5rem',
                      }}
                    >
                      <small
                        style={{
                          fontSize: '11pt',
                          color: remaining <= 10 ? 'red' : auxColor,
                        }}
                      >
                        Caracteres restantes: {remaining}
                      </small>
                    </div>
                  </div>
                  <div>
                    <strong>
                      Vídeo{' '}
                      <small
                        style={{
                          fontSize: '11pt',
                        }}
                      >
                        (opcional)
                      </small>
                    </strong>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        position: 'relative',
                      }}
                    >
                      <input
                        type="url"
                        defaultValue={pool ? pool.videoUrl : undefined}
                        value={formData.videoUrl}
                        onChange={(e) => {
                          setFormData({ ...formData, videoUrl: e.target.value });
                        }}
                        placeholder={`Url do vídeo do youtube ou vimeo`}
                      />
                      {isValidVideo(formData.videoUrl ?? '') && (
                        <span onClick={() => setModalPoolViedo(true)} className="player-button" title="Reproduzir vídeo">
                          <MdOutlineVideoLibrary />
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <strong>Categoria</strong>
                    <select onChange={onCategoryChanged}>
                      <option value="" disabled selected hidden>
                        Selecione uma categoria
                      </option>
                      {categories.map((item, index) => (
                        <option selected={item.id === selectedCategoryId} key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <strong>Localização</strong>
                    <div>
                      <select
                        onChange={(e) => {
                          if (e.target.value) setFormData({ ...formData, state: e.target.value });
                        }}
                        value={mode === 'edit' ? formData.state : undefined}
                      >
                        <option value="" disabled selected hidden>
                          Selecione um estado
                        </option>
                        {states.map((item, index) => (
                          <option selected={item.value === pool.state} key={index} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <strong>
                      Personalizar endereço do meu site{' '}
                      <small
                        style={{
                          fontSize: '11pt',
                        }}
                      >
                        (opcional)
                      </small>
                    </strong>
                    <input
                      type="text"
                      defaultValue={pool ? pool.slug : undefined}
                      value={formData.slug}
                      onBlur={(e) => {
                        if (e.currentTarget.value !== pool.slug) handleVerifySlug(e.currentTarget.value);
                      }}
                      onChange={(e) => {
                        if (!/^[a-zA-Z0-9]+$/.test(e.target.value) && e.target.value !== '') return;
                        setFormData({ ...formData, slug: e.target.value });
                      }}
                      style={
                        !isValidSlug
                          ? {
                            border: 'solid 1px red',
                            color: 'red',
                          }
                          : undefined
                      }
                      placeholder={`Digite um ou dois nomes sem espaço e sem acentos`}
                    />
                    <span
                      style={{
                        color: 'red',
                        marginTop: '1rem',
                        display: !isValidSlug ? 'block' : 'none',
                      }}
                    >
                      Slug indisponível ou inválido! Por favor, escolha outro.
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <strong
                      style={{
                        fontSize: '11pt',
                        backgroundColor: newBackGroundColor,
                        marginRight: '.5rem',
                        opacity: 0.8,
                      }}
                    >
                      O endereço do seu site será:
                    </strong>
                    <small
                      style={{
                        fontSize: '11pt',
                        color: auxColor,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      https://nossavaquinha.com.br/{formData.slug}
                    </small>
                  </div>
                  <div>
                    <strong>Valor a ser arrecadado</strong>
                    <CurrencyInput
                      placeholder="R$ 0,00"
                      defaultValue={mode === 'edit' ? pool.target : undefined}
                      onFocus={() => {
                        setFormData({
                          ...formData,
                          target: '',
                        });
                      }}
                      onBlur={() => {
                        setFormData({
                          ...formData,
                          target: `${lastValue},00`,
                        });
                      }}
                      onValueChange={handleOnValueChange}
                      value={formData.target}
                      allowDecimals
                      decimalScale={2}
                      decimalSeparator=","
                      decimalsLimit={2}
                      allowNegativeValue={false}
                      intlConfig={{ locale: 'pt-br', currency: 'BRL' }}
                    />
                  </div>
                  <div>
                    <strong>Mensagem de apresentação</strong>
                    <textarea
                      defaultValue={pool ? pool.description : undefined}
                      value={formData.description}
                      placeholder="Conte brevemente sua história e solicite apoio"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        });
                      }}
                    />
                  </div>
                  <div>
                    {pool.id && (
                      <>
                        <span>
                          <a href={pool.slug ? '/' + pool.slug : '/#'} target={'_blank'} rel="noreferrer">
                            <button disabled={mode !== 'edit'}>Ver seu site</button>
                          </a>
                        </span>
                        <span>
                          <button
                            onClick={() => {
                              handlerShareData();
                            }}
                            disabled={mode !== 'edit'}
                          >
                            Compartilhar
                          </button>
                        </span>

                      </>
                    )}
                    <span
                      style={{
                        position: 'relative',
                      }}
                    >
                      <button
                        onClick={(e) => {
                          (pickColor?.current as any).click();
                        }}
                        className="button-color"
                      >
                        <input
                          ref={pickColor}
                          onChange={(e) => {
                            setNewBackGroundColor(e.target.value);
                            (pickColor.current as any).value = '';
                          }}
                          type="color"
                          list="presetColors"
                        />
                        <datalist id="presetColors">
                          {colors.map((item, index) => (
                            <option key={index}>{item}</option>
                          ))}
                        </datalist>
                        <div className="picker-svg">
                          <Image src={ColorPickerIcon} alt="color-picker" />
                        </div>
                        <span>Editar cor</span>
                      </button>
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  {isGuest && isValidVideo(pool.videoUrl ?? '') && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        position: 'relative',
                        alignItems: 'center',
                        marginBottom: '3rem',
                      }}
                      onClick={() => setModalPoolViedo(true)}
                    >
                      <span
                        style={{
                          position: 'relative',
                          top: 0,
                          left: 0,
                          margin: 0,
                        }}
                        className="player-button"
                        title="Reproduzir vídeo"
                      >
                        <MdOutlineVideoLibrary />
                      </span>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          marginLeft: '1rem',
                          cursor: 'pointer',
                        }}
                      >
                        <span
                          style={{
                            color: fontColor,
                            fontSize: '2em',
                            lineHeight: 1,
                          }}
                        >
                          Vídeo
                        </span>
                        <small>Clique para reproduzir o vídeo da vaquinha.</small>
                      </div>
                    </div>
                  )}
                  <p
                    ref={descriptionContainerRef}
                    style={{
                      overflowWrap: 'anywhere',
                      textAlign: 'justify',
                    }}
                  >
                    {pool.description}
                  </p>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <PoolProgressiveCard
                  isOnDescription={isOnDescription}
                  isOnBottom={isOnBottom}
                  isPrivate={!isGuest}
                  fontColor={getSvgColor}
                  data={pool}
                  amount={Number(amount)}
                  onDonate={handleDonate}
                  onMounted={(data) => setProgressiveData(data)}
                />

                {isPoolManagerUser(pool, user?.id) && mode === 'edit' && pool.active !== false && (
                  <div style={{ marginTop: '20px', textAlign: 'center', width: '95%' }}>
                    <button
                      onClick={() => setShowEndCampaignModal(true)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'background-color 0.3s ease',
                        width: '90%',
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                    >
                      Encerrar Campanha
                    </button>
                  </div>
                )}
              </div>
            </div>
          </ProgressiveSection>

          {isGuest && (
            <>
              <QuotasSection fontColor={fontColor}>
                <div>
                  <h2>Faça uma doação</h2>
                </div>
                <ContainerDonationSuggestions>
                  <ButtonDonnationValue selected={amount === '20'} onClick={() => {
                    setAmount('20');
                    handleDonateWithValue(20);
                  }}>
                    {formatarValorParaReais(2000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '30'} onClick={() => {
                    setAmount('30');
                    handleDonateWithValue(30);
                  }}>
                    {formatarValorParaReais(3000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '40'} onClick={() => {
                    setAmount('40');
                    handleDonateWithValue(40);
                  }}>
                    {formatarValorParaReais(4000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '50'} onClick={() => {
                    setAmount('50');
                    handleDonateWithValue(50);
                  }}>
                    {formatarValorParaReais(5000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '60'} onClick={() => {
                    setAmount('60');
                    handleDonateWithValue(60);
                  }}>
                    {formatarValorParaReais(6000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '70'} onClick={() => {
                    setAmount('70');
                    handleDonateWithValue(70);
                  }}>
                    {formatarValorParaReais(7000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '100'} onClick={() => {
                    setAmount('100');
                    handleDonateWithValue(100);
                  }}>
                    {formatarValorParaReais(10000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '200'} onClick={() => {
                    setAmount('200');
                    handleDonateWithValue(200);
                  }}>
                    {formatarValorParaReais(20000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '300'} onClick={() => {
                    setAmount('300');
                    handleDonateWithValue(300);
                  }}>
                    {formatarValorParaReais(30000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '500'} onClick={() => {
                    setAmount('500');
                    handleDonateWithValue(500);
                  }}>
                    {formatarValorParaReais(50000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '700'} onClick={() => {
                    setAmount('700');
                    handleDonateWithValue(700);
                  }}>
                    {formatarValorParaReais(70000)}
                  </ButtonDonnationValue>
                  <ButtonDonnationValue selected={amount === '1000'} onClick={() => {
                    setAmount('1000');
                    handleDonateWithValue(1000);
                  }}>
                    {formatarValorParaReais(100000)}
                  </ButtonDonnationValue>
                </ContainerDonationSuggestions>
                <div>
                  <div>
                    <DonateInputContainer
                      style={{
                        marginBottom: '0rem',
                      }}
                    >
                      <div>
                        <strong>Digite aqui o valor de sua doação</strong>
                      </div>
                      <CurrencyInput
                        id="donateInput"
                        placeholder="R$ 0,00"
                        onFocus={() => {
                          setAmount('');
                        }}
                        onValueChange={handleOnValueAmountChange}
                        value={amount}
                        allowDecimals
                        decimalScale={2}
                        decimalSeparator=","
                        decimalsLimit={2}
                        min={20}
                        max={10000}
                        allowNegativeValue={false}
                        intlConfig={{ locale: 'pt-br', currency: 'BRL' }}
                        style={{
                          marginBottom: '0rem',
                        }}
                      />
                      {isPoolVerified(pool) && (
                        <VerificationContainer>
                          <img
                            src="/verificada.png"
                            alt="Verificada"
                            width="40"
                            height="40"
                            style={{
                              width: 200,
                              height: 150,
                            }}
                          />
                        </VerificationContainer>
                      )}
                    </DonateInputContainer>
                    <div style={{ marginTop: '10px' }}>
                      <Button onClick={handleDonate}>{pool.active ? 'Quero doar' : 'Campanha encerrada'}</Button>
                    </div>
                  </div>
                </div>
              </QuotasSection>
              <TaxValuesTextNote>*Valores sem impostos</TaxValuesTextNote>
            </>
          )}

          {(isGuest || showOwnerSupporterExtras) && (
            <Supporters color={headerFontColor}>
              <h2>Apoiadores</h2>
              {supportes.length ? (
                <div
                  style={{
                    flex: 1,
                    flexDirection: 'column',
                  }}
                >
                  <SupporterList>
                    {supportes.map((item, index) => {
                      const hinted: Supportes =
                        item.id && supporterPaymentHints[item.id]
                          ? { ...item, payment_method: supporterPaymentHints[item.id] }
                          : item;
                      const viaLine = showOwnerSupporterExtras ? supporterPaymentViaLine(hinted) : null;
                      return (
                        <SupporterItem key={index}>
                          <div
                            style={{
                              marginRight: 8,
                            }}
                          >
                            <img
                              style={{
                                backgroundColor: 'white',
                                padding: 4,
                                borderRadius: 8,
                              }}
                              src="/vaquinha.png"
                              alt="porquinho"
                            />
                          </div>
                          <div>
                            <div>
                              <strong>{sanitizeDisplayName(item.name)}</strong>
                              <br />
                              <p>{item.paidAt && new Date(parseInt(item.paidAt)).toLocaleDateString()}</p>
                              {viaLine && <p>{viaLine}</p>}
                            </div>
                            <span>
                              {item.splitAmount &&
                                parseFloat(((item.splitAmount + pool.fixedFee) / 100)?.toString() || '')?.toLocaleString('pt-br', {
                                  style: 'currency',
                                  currency: 'BRL',
                                })}
                            </span>
                          </div>
                        </SupporterItem>
                      );
                    })}
                  </SupporterList>
                  <PaginateContainer>
                    <PaginationButtons
                      onPress={() => fetchAddedSupporters(pool.id as string)}
                      currentPage={currentPage ?? 1}
                      totalPages={totalSupportesPages ?? 0}
                      onNextPage={() => pool.id && handleNextSupportesPage?.(pool.id)}
                      onPreviewPage={() => pool.id && handlePreviewSupportesPage?.(pool.id)}
                    />
                  </PaginateContainer>
                </div>
              ) : (
                <span>Ainda não há apoiadores</span>
              )}
            </Supporters>
          )}

          {!isGuest ? (
            <PrivateAlbumSection borderColor={fontColor}>
              <div>
                <div>
                  <div>
                    <h4>Álbum de fotos</h4>
                  </div>
                  <div>
                    {imgsPool.length > 0 && (
                      <Swiper slidesPerView={'auto'} spaceBetween={10} grabCursor={true} freeMode={true} modules={[FreeMode]}>
                        {imgsPool?.map((item: any) => (
                          <SwiperSlide
                            style={{
                              width: 184,
                              height: 184,
                            }}
                            key={item.id}
                          >
                            <div className="card" onClick={() => { }}>
                              <img src={item.url} alt={item.url} />

                              <span>
                                <FaTrash onClick={() => handleRemoveImg(item.id)} color="#fff" size={18} />
                              </span>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    )}
                    {imgsPool.length === 0 && <Image src={PreviewPhoto} width={100} height={96} alt="preview photo" />}
                  </div>
                  <div>
                    <button onClick={() => toggleModalImageCropper('album')}>Adicionar Fotos</button>
                  </div>
                </div>
              </div>
            </PrivateAlbumSection>
          ) : (
            imgsPool.length > 0 && (
              <PublicAlbumSection pageSize={publicAlbumPages}>
                <div>
                  <h2 className="title">Álbum de fotos</h2>
                  {imgsPool.length > 0 && (
                    <Swiper
                      style={{
                        width: '100vw',
                      }}
                      slidesPerView={'auto'}
                      onPaginationRender={(_, pagination) => setPublicAlbumPages(pagination.childElementCount)}
                      spaceBetween={30}
                      modules={[Pagination]}
                      pagination={true}
                    >
                      {imgsPool?.map((item: any) => (
                        <SwiperSlide
                          style={{
                            width: 'fit-content',
                            height: 400,
                          }}
                          key={item.id}
                        >
                          <div className="card">
                            <img src={item.url} alt={item.url} />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  )}
                  {imgsPool.length === 0 && <Image src={PreviewPhoto} width={100} height={96} alt="preview photo" />}
                </div>
              </PublicAlbumSection>
            )
          )}

          <MessageSection>
            <h2>Mensagens Recebidas</h2>
            {messages.length > 0 ? (
              <Swiper
                pagination={{
                  bulletActiveClass: 'bulletActive',
                }}
                slidesPerView={'auto'}
                spaceBetween={30}
                modules={[Pagination]}
              >
                {messages.map((item) => (
                  <SwiperSlide
                    style={{
                      width: 400,
                      marginBottom: 20,
                    }}
                    key={item.id}
                  >
                    <div className="message">
                      <p>{item.content}</p>
                      <span>
                        {sanitizeDisplayName(item.name)}
                        {!isGuest && (
                          <span>
                            <Switch>
                              <input
                                type="checkbox"
                                id={item.id}
                                checked={item.visible}
                                onChange={() => handleModerateMessage(item.id, item.visible)}
                              />
                              <label htmlFor={item.id}>Aceitar</label>
                            </Switch>
                            <small>Aceitar</small>
                          </span>
                        )}
                      </span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <CentredSpan>Ainda não há mensagens</CentredSpan>
            )}
          </MessageSection>

          {isGuest && (
            <>
              <SendMessageSection fontColor={fontColor}>
                <div>
                  <label>Nome:</label>
                </div>
                <div>
                  <input
                    placeholder="Nome completo"
                    value={messageForm.name}
                    onChange={(e) => {
                      setMessageForm({
                        ...messageForm,
                        name: e.target.value,
                      });
                    }}
                  />
                </div>
                <div>
                  <label>Deixe aqui sua mensagem:</label>
                </div>
                <div>
                  <textarea
                    placeholder="As mensagens recebidas serão exibidas aqui"
                    value={messageForm.content}
                    onChange={(e) => {
                      setMessageForm({
                        ...messageForm,
                        content: e.target.value,
                      });
                    }}
                  />
                </div>
                <div>
                  <Button
                    onClick={() => {
                      pool.id && handlerSendMessage(pool.id);
                    }}
                  >
                    Enviar
                  </Button>
                </div>
              </SendMessageSection>
              <ShareSection>
                <div>
                  <h2>Ajude a divulgar a campanha</h2>
                </div>
                <div>
                  <Button onClick={() => handlerShareData()}>Compartilhar</Button>
                </div>
              </ShareSection>
            </>
          )}

          <section style={{ overflow: 'hidden', margin: '8rem 0' }}>
            {isGuest && (
              <PaymentSecurity
                color={
                  newBackGroundColor !== '#fff6e8'
                    ? distance < similarityThreshold && isGuest
                      ? distance < similarityThreshold
                        ? headerFontColor
                        : headerBgColor
                      : isGuest
                        ? headerBgColor
                        : headerFontColor
                    : '#174F68'
                }
              />
            )}
          </section>

          <FooterSection
            bgColor={newBackGroundColor}
            imageURL={footerBgImage || BgFooterDefaultImage.src}
            mobileCover={footerBgImage || BgFooterDefaultMobileImage.src}
          >
            {!isGuest && (
              <InsertImageButton onClick={() => toggleModalImageCropper('footer')} label="Alterar imagem de rodapé" arrowPosition="down" />
            )}
          </FooterSection>
        </Content>
        <Footer
          style={{
            marginBottom: submit && !isGuest ? 70 : 0,
          }}
          bgColor={getSvgColor}
          fontColor={
            distance < similarityThreshold && isGuest
              ? distance < similarityThreshold
                ? headerFontColor
                : headerBgColor
              : isGuest
                ? headerBgColor
                : headerFontColor
          }
        >
          <LogoItems>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                forceNavigation('/');
              }}
            >
              <img src={distance < similarityThreshold ? '/Logo.svg' : '/Logo-white.svg'} height={60} alt="logo-white" />
            </a>
            <p className="web-copyright">© {year} - Todos os direitos reservados.</p>
            <p className="mobile-copyright">© {year} - Todos os direitos reservados.</p>
          </LogoItems>
          <CenterLinks>
            <MobileInstaLogo>
              <ul>
                {socialNetwork.map(({ icon, url }, index) => (
                  <li key={index} style={{ listStyle: 'none', cursor: 'pointer' }}>
                    <a target="_blank" rel="noreferrer" href={url} style={{ display: 'flex', textDecoration: 'none' }}>
                      <FaInstagram
                        size={25}
                        color={
                          distance < similarityThreshold && isGuest
                            ? distance < similarityThreshold
                              ? headerFontColor
                              : headerBgColor
                            : isGuest
                              ? headerBgColor
                              : headerFontColor
                        }
                      />
                      <p
                        style={{
                          marginLeft: '.5rem',
                          marginTop: '.2rem',
                          color:
                            distance < similarityThreshold && isGuest
                              ? distance < similarityThreshold
                                ? headerFontColor
                                : headerBgColor
                              : isGuest
                                ? headerBgColor
                                : headerFontColor,
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontFamily: 'Nunito',
                        }}
                      >
                        Instagram
                      </p>
                    </a>
                  </li>
                ))}
              </ul>
            </MobileInstaLogo>
            <a
              href={`/report/${pool.slug}`}
              onClick={(e) => {
                e.preventDefault();
                forceNavigation(`/report/${pool.slug}`);
              }}
              style={{
                textDecoration: 'none',
                color: 'white',
              }}
            >
              <p>Denúncia</p>
            </a>
            <a
              href="/termos-e-condicoes"
              onClick={(e) => {
                e.preventDefault();
                forceNavigation('/termos-e-condicoes');
              }}
              style={{
                textDecoration: 'none',
                color: 'white',
              }}
            >
              <p>Termos de uso</p>
            </a>
            <a
              href="/privacy-policy"
              onClick={(e) => {
                e.preventDefault();
                forceNavigation('/privacy-policy');
              }}
              style={{
                textDecoration: 'none',
                color: 'white',
              }}
            >
              <p>Política de privacidade</p>
            </a>
          </CenterLinks>
          <InstaLogo>
            <ul>
              {socialNetwork.map(({ icon, url }, index) => (
                <li key={index}>
                  <a target="_blank" rel="noreferrer" href={url} style={{ display: 'flex', textDecoration: 'none' }}>
                    <FaInstagram
                      size={25}
                      color={
                        distance < similarityThreshold && isGuest
                          ? distance < similarityThreshold
                            ? headerFontColor
                            : headerBgColor
                          : isGuest
                            ? headerBgColor
                            : headerFontColor
                      }
                    />
                    <p
                      style={{
                        marginLeft: '.5rem',
                        marginTop: '.2rem',
                        color:
                          distance < similarityThreshold && isGuest
                            ? distance < similarityThreshold
                              ? headerFontColor
                              : headerBgColor
                            : isGuest
                              ? headerBgColor
                              : headerFontColor,
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontFamily: 'Nunito',
                      }}
                    >
                      Instagram
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </InstaLogo>
        </Footer>
      </Container>
      {!isGuest && (
        <SubmitContainer
          style={{
            bottom: !submit ? '-7rem' : 0,
          }}
        >
          <div>
            <strong>{mode === 'edit' ? 'Salvar alterações?' : 'Olá! Já terminou de personalizar tudo?'}</strong>
            <div>
              <Button onClick={mode === 'edit' ? handlerEditPool : handlerPool}>{mode === 'edit' ? 'Sim' : 'Criar vaquinha'}</Button>
              {mode === 'edit' && (
                <Button
                  style={{
                    marginLeft: '1rem',
                    backgroundColor: '#174F68',
                  }}
                  onClick={handlerResetPool}
                >
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        </SubmitContainer>
      )}
    </ReCAPTCHAProvider>
  );
};

export default PoolTemplate;
